import React from 'react';

import type { CodeBlockRedlineInsert } from '@aline/common';
import { Typography } from '@mui/material';
import { equals } from 'ramda';
import { useSuggestionGroupNavigationHooks } from 'src/editor/EditorAlert/SuggestionsAlert/useNavigationHooks';
import { HtmlPreview } from 'src/utils/HtmlPreview';
import { suggestHtmlPreviewStyles } from 'src/utils/preview-styles';
import { makeStyles } from 'tss-react/mui';

import {
  ExpandableRedlineCard,
  RedlinePreviewSection,
  RedlineReasoning,
} from './ExpandableRedlineCard';
import type { SuggestionStatus } from '../../../../chat-types';
import { createAIChatItem } from '../../../helpers/aiChatItem';

const useStyles = makeStyles()(theme => ({
  /**
   * The reference paragraph the insertion is anchored to: plain body text,
   * muted so the inserted content stays the focus of the preview.
   */
  anchorPreview: {
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
    wordBreak: 'break-word',
  },
  /**
   * The inserted content, styled to match the "insert" segments of the
   * edit-paragraph cards: a plain white card whose text reads as a suggested
   * insert — blue, underlined, with the tinted background hugging the text
   * (via `fit-content`) rather than filling the whole card.
   */
  htmlPreview: suggestHtmlPreviewStyles(theme, 'insert'),
  previewBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
}));

interface RedlineInsertItemProps {
  insert: CodeBlockRedlineInsert;
  messageId: string;
  /**
   * The currently selected groupId for navigation highlighting. When it matches
   * the insertion's groupId, the card is visually highlighted.
   */
  selectedGroupId: string | null;
  status: SuggestionStatus;
  threadId: string;
}

/**
 * Renders a single `redline-insert` (new content the AI suggested adding to the
 * document) as an expandable card, mirroring {@link RedlineActionItem}:
 * - Header with the reasoning and status chip
 * - Expandable body with the full reasoning and a preview of the inserted text
 *   next to the reference paragraph it is anchored to
 */
export const RedlineInsertItem = React.memo(
  function RedlineInsertItem({
    insert,
    messageId,
    selectedGroupId,
    status,
    threadId,
  }: RedlineInsertItemProps) {
    const isSelected = selectedGroupId === insert.groupId;
    const { handleNavigateToRedlineAction } = useSuggestionGroupNavigationHooks();

    const elementId = createAIChatItem({
      groupId: insert.groupId,
      messageId,
      threadId,
      type: 'suggestion',
    });

    const previewLabel =
      insert.position === 'before' ? 'New text (inserted before):' : 'New text (inserted after):';

    return (
      <ExpandableRedlineCard
        elementId={elementId}
        isSelected={isSelected}
        status={status}
        title={insert.reasoning}
        onCardClick={() =>
          handleNavigateToRedlineAction({
            documentId: insert.documentId,
            groupId: insert.groupId,
            locationAnchor: insert.locationAnchor,
          })
        }
      >
        <RedlineReasoning reasoning={insert.reasoning} />
        <RedlinePreviewSection title={previewLabel}>
          <InsertPreviewBody insert={insert} />
        </RedlinePreviewSection>
      </ExpandableRedlineCard>
    );
  },
  // Deep equality check to avoid re-renders during streaming
  (prev, next) => equals(prev, next)
);

/**
 * The insertion and its reference paragraph in document order, so the card
 * shows where the new content lands: the paragraph above the new content when
 * the content goes after it, below when the content goes before it.
 */
function InsertPreviewBody({ insert }: { insert: CodeBlockRedlineInsert }): JSX.Element {
  const { classes } = useStyles();
  const insertedHtml = <HtmlPreview className={classes.htmlPreview} html={insert.html} />;

  if (insert.anchorPreview === undefined) return insertedHtml;

  const anchor = (
    <Typography className={classes.anchorPreview} variant="body2">
      {insert.anchorPreview}
    </Typography>
  );

  if (insert.position === 'after') {
    return (
      <div className={classes.previewBody}>
        {anchor}
        {insertedHtml}
      </div>
    );
  }

  return (
    <div className={classes.previewBody}>
      {insertedHtml}
      {anchor}
    </div>
  );
}
